import React, {useState, useEffect, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import styles from './ShopReviewOrder.module.scss';
import gemsImgBackground from '../../assets/images/gems/shop_gems_background.png';
import ArrowGoBack from '../../assets/images/arrow_go_back.png';
import WarToken from '../../assets/images/war_token.png';
import MarketingCampainBanner from '../../assets/images/marketing_campain_banner.png';

import {ENV, OFFER_TYPE_TO_NAME, WEBSITE_URL_TERMS} from "../../shared/constants";
import {CSSTransition} from "react-transition-group";
import ButtonNew from "../../components/buttons/ButtonNew/ButtonNew";
import {formatNumber, GA, trackAbandonOfferDetails, validateEmail} from '../../shared/helpers';

import Gambit from '../../components/Gambing/Gambit';
import { OFFER_TYPES } from '../../shared/constants';
import OfferInfo from './OfferInfo';

/**
 * ShopReviewOrder Component
 * 
 * 
 * Displays the order review page where users can review their selected offer,
 * agree to terms and conditions, optionally provide email for marketing/receipt,
 * and proceed to checkout.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.OldPrice - Original price of the offer before discount
 * @param {number} props.NewPrice - Discounted/current price of the offer
 * @param {string} props.Type - Type of the offer (e.g., gems, resources, crates)
 * @param {string} props.ImageURL - URL of the offer image (if available)
 * @param {string} props.mappedImg - Mapped image source for the offer icon
 * @param {string} props.mappedName - Display name of the offer
 * @param {number} props.mappedQuantity - Quantity of items in the offer
 * @param {Function} props.setSelectedOffer - Function to set the selected offer
 * @param {Object} props.config - Configuration object containing localized text
 * @param {Object} props.windowSize - Object containing window size information
 * @param {string} props.currency - Currency symbol/code for pricing display
 * @param {number} props.WarTokens - Number of war tokens included in the offer
 * @param {string} props.imgBackground - Background image for the offer display
 * @param {boolean} props.IsMarketingEmailGiven - Whether user has already provided marketing email consent
 * @param {Object} props.selectedOffer - The currently selected offer object
 * @param {Function} props.removeSelectedOffer - Function to remove/deselect the current offer
 * @returns {JSX.Element} The rendered review order page
 */
const ShopReviewOrder = ({
    OldPrice,
    NewPrice,
    Type,
    ImageURL,
    mappedImg,
    mappedName,
    mappedQuantity,
    setSelectedOffer,
    config,
    windowSize,
    currency,
    WarTokens,
    imgBackground,
    IsMarketingEmailGiven,
    selectedOffer,
    removeSelectedOffer
}) => {
    const navigate = useNavigate();
    
    // Track if this is the first page view to prevent duplicate GA events
    let isFirstPageView = useRef(true);
    
    // State for user agreements and email input
    const [isAgreed, setIsAgreed] = useState(false); // Terms and conditions agreement
    const [isAgreedForMarketingEmail, setIsAgreedForMarketingEmail] = useState(false); // Marketing emails consent
    const [doesWantReceipt, setDoesWantReceipt] = useState(false); // Receipt via email preference
    const [emailInput, setEmailInput] = useState(''); // Email address input

    // Construct the offer image component based on available data
    let Image = null;
    if (ImageURL) {
        // Use provided image URL for offers with custom images
        Image = <img src={ImageURL} alt={''} className={styles.offerImg} />
    } else {
        // Build composite image for standard offers (e.g., gems, resources)
        Image = <div className={styles.gemsImgWrapper}>
            <img src={imgBackground || gemsImgBackground} alt={''} className={styles.imgBackground} />
            <div className={styles.name}>{mappedName}</div>
            <div className={styles.quantity}>{mappedQuantity && mappedQuantity.toLocaleString()}</div>
            <img src={mappedImg} alt={''} className={`${styles.gemsImage} ${OFFER_TYPE_TO_NAME[Type]}`} />
        </div>
    }
    
    /**
     * Handles removing the selected offer and tracks abandonment in GA
     */
    const handleRemoveOffer = () => {
        if (ENV === 'live') {
            GA.trackAbandonOfferDetails(selectedOffer);
        }
        removeSelectedOffer();
    }

    /**
     * Proceeds to checkout after validating user inputs
     * - Checks if terms and conditions are agreed to
     * - Validates email if receipt or marketing emails are requested
     * - Saves email to localStorage
     * - Tracks checkout event in GA
     * - Navigates to checkout page with email preferences
     */
    const proceed = () => {
        // Validate terms and conditions agreement
        if (!isAgreed) {
            toast.error(config.notAgreedWithTermsAndCond);
            return;
        }
        
        // Validate email format if user wants receipt or marketing emails
        if ((doesWantReceipt || isAgreedForMarketingEmail) && !validateEmail(emailInput)) {
            toast.error(config.invalidEmail);
            return;
        }

        // Retrieve and update player info with email
        let player = localStorage.getItem('playerInfo');
        player = JSON.parse(player);

        const updatePlayerInfo = { ...player };
        updatePlayerInfo.email = emailInput;
        localStorage.setItem(
            'playerInfo',
            JSON.stringify(updatePlayerInfo)
        );

        // Track checkout event in Google Analytics
        const selectedOfferGA = {...selectedOffer};
        selectedOfferGA.Name = OFFER_TYPE_TO_NAME[Type];
        GA.trackBeginCheckout(selectedOfferGA);

        // Navigate to checkout with email preferences
        navigate('/checkout', { state: {
            saveEmailAgreed: IsMarketingEmailGiven ? null : isAgreedForMarketingEmail,
            wantsReceipt: doesWantReceipt,
            email: emailInput.length > 0 ? emailInput : null,
        }});
    }

    /**
     * Handles browser back button navigation
     * Tracks offer abandonment in GA and removes selected offer
     * 
     * @param {Event} event - Browser popstate event
     */
    const handleGoBack = useCallback((event) => {
        if (event) {
            GA.trackAbandonOfferDetails(selectedOffer);

            event.preventDefault();
            removeSelectedOffer();
        }
    }, []);

    // Initialize component: scroll to top and load saved email from localStorage
    useEffect(() => {
        window.scrollTo(0, 0);

        let player = localStorage.getItem('playerInfo');
        player = JSON.parse(player);

        if (player && player.email) {
            setEmailInput(player.email);
        }
    }, []);

    // Set up browser back button handler
    useEffect(() => {
        window.addEventListener('popstate', (event) => handleGoBack(event), false);

        return () => {
            window.removeEventListener('popstate', handleGoBack);
        }
    }, []);

    // Track offer details view in GA on initial render only
    useEffect(() => {
        if (isFirstPageView.current) {
            const selectedOfferGA = {...selectedOffer};
            selectedOfferGA.Name = OFFER_TYPE_TO_NAME[Type];
            if (ENV === 'live') {
                GA.trackViewOfferDetails({ selectedOfferGA });
            }
            isFirstPageView.current = false;
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            {/* Header with back button and title */}
            <CSSTransition in appear timeout={300} classNames={'fade-in-slide-down'}>
                <div className={styles.titleWrapper}>
                    <img
                        className={styles.arrowGoBack}
                        src={ArrowGoBack}
                        alt={''}
                        onClick={() => {
                             if (ENV === 'live') {
                                 GA.trackAbandonOfferDetails(selectedOffer);
                             }
                            removeSelectedOffer();
                         }}
                     />
                    <div className={styles.title}>{config.reviewOrder}</div>
                </div>
            </CSSTransition>
            <div className={'dividerHorizontal'}></div>
           { 
            // Render different layouts based on offer type
            OFFER_TYPES.gambitLeaguePackages !== Type ?
            <div className={styles.offerInfo}>
                {Image}
                <OfferInfo
                    Type={OFFER_TYPE_TO_NAME[Type]}
                    OldPrice={OldPrice}
                    NewPrice={NewPrice}
                    currency={currency}
                    WarTokens={WarTokens}
                    WarToken={WarToken}
                    removeOffer={handleRemoveOffer}
                    isMobileView={windowSize.isMobileView}
                    removeButtonText={config.remove}
                />
            </div>
            :
            // Special layout for Gambit League packages
            <div className={`${styles.offerInfo} ${styles.offerDobuleItemInfo}`}>
                <Gambit {...selectedOffer} currency={currency} config={config}  isReviewOrder={true}/>
                <OfferInfo
                    Type={OFFER_TYPE_TO_NAME[Type]}
                    OldPrice={OldPrice}
                    NewPrice={NewPrice}
                    currency={currency}
                    WarTokens={WarTokens}
                    WarToken={WarToken}
                    removeOffer={handleRemoveOffer}
                    isMobileView={windowSize.isMobileView}
                    removeButtonText={config.remove}
                />
            </div>
           }
            <div className={'dividerHorizontal'}></div>
            {/* Price breakdown section */}
            <div className={styles.discountWrapper}>
                <div className={styles.discountInfo}>
                    <div className={styles.names}>
                        <div>{config.item}</div>
                        {OldPrice !== NewPrice ?
                            <div>{config.discount}</div>
                            :
                            null
                        }
                    </div>
                    <div className={styles.values}>
                        <div>{OldPrice.toFixed(2)} {currency}</div>
                        {(OldPrice - NewPrice) !== 0 ?
                            <div>-{(OldPrice - NewPrice).toFixed(2)} {currency}</div>
                            :
                            null
                        }
                    </div>
                </div>
                <div className={'dividerHorizontal'}></div>
            </div>
            {/* Order total */}
            <div className={styles.total}>{config.orderTotal}&nbsp;&nbsp;&nbsp;{NewPrice.toFixed(2)} {currency}</div>
            
            {/* User agreements and email input section */}
            <div className={styles.wrapperUserInteractions}>
                {/* Terms and conditions checkbox */}
                <div className={styles.agree} onClick={() => setIsAgreed(!isAgreed)}>
                    <input type='checkbox' checked={isAgreed}/>
                    <a href={WEBSITE_URL_TERMS} className={styles.terms}><span>{config.agreeWithTermsAndCond}</span></a>
                </div>
                {/* Marketing email section - only show if user hasn't already given consent */}
                {IsMarketingEmailGiven ?
                    null
                :
                    <>
                        <div className={styles.promoBannerMarketing}>
                            <img src={MarketingCampainBanner} alt={''}/>
                            <span>{config.marketingEmailBanner}</span>
                        </div>
                        <div className={styles.wrapperEmailMarketing}>
                            <div className={styles.agree}
                                 onClick={() => setIsAgreedForMarketingEmail(!isAgreedForMarketingEmail)}>
                                <input type='checkbox' checked={isAgreedForMarketingEmail}/>
                                <span className={styles.terms}>{config.receiveMarketingEmails}</span>
                            </div>
                        </div>
                    </>
                }
                {/* Email receipt section */}
                <div className={styles.wrapperEmailReceipt}>
                    <div className={styles.agree} onClick={() => setDoesWantReceipt(!doesWantReceipt)}>
                        <input type='checkbox' checked={doesWantReceipt}/>
                        <span className={styles.terms}>{config.wantReceipt}</span>
                    </div>
                    {/* Show email input if user wants receipt or marketing emails */}
                    {doesWantReceipt ||  isAgreedForMarketingEmail ?
                        <input
                            placeholder={config.enterEmail}
                            className={styles.enterEmail}
                            value={emailInput}
                            type="email"
                            autoComplete="email"
                            name="email"
                            onChange={(e) => setEmailInput(e.target.value)}
                        />
                    :
                        null
                    }
                </div>
            </div>
            {/* Checkout button */}
            <div className={styles.buttonWrapper}>
                <ButtonNew text={config.proceedToCheckout} isDisabled={!isAgreed} onClick={proceed}/>
            </div>
        </div>
    );
};

export default ShopReviewOrder;